import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Text, TextLink } from '@factorialco/deprecated-design-system'
import {
  AtsFeedbackDetailFragment,
  EvaluationFormListItemFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EvaluationFormDisplay } from '../../EvaluationForm/components/EvaluationFormDisplay'
import { EvaluationFormEdit } from '../../EvaluationForm/components/EvaluationFormEdit'
import { FeedbackDisplay } from './FeedbackDisplay'
import { FeedbackEdit } from './FeedbackEdit'

interface Props {
  feedback: AtsFeedbackDetailFragment
  evaluationForm?: EvaluationFormListItemFragment
  enabledScoring?: boolean
}

const FeedbackCreationInformation = ({ feedback }: Props) => {
  const { pathname } = useLocation()
  const { navigation } = useNavigation()

  const { application, applicationPhase } = feedback
  const feedbackDate = stringToDate(feedback.createdAt, 'backend_datetime').fromNow()

  if (!application || !applicationPhase) return <>{feedbackDate}</>

  const urlParams = {
    jobId: application.jobPosting.id,
    application_id: application.id,
  }
  const applicationBaseUrl = navigation.ats.jobs.jobsId.applications.applicationDetail

  return (
    <>
      {i18n.tView('ats.feedbacks.date_phase_job_posting', {
        date: <>{feedbackDate}</>,
        application_phase: <>{applicationPhase.name}</>,

        job_posting_title:
          pathname === applicationBaseUrl.notes.fullPath(urlParams) ? (
            <Text.Normal color="grey1000" size="200">
              {application.jobPosting.title}
            </Text.Normal>
          ) : (
            <TextLink to={applicationBaseUrl.index.fullPath(urlParams)}>
              {application.jobPosting.title}
            </TextLink>
          ),
      })}
    </>
  )
}

export const Feedback = ({ feedback, evaluationForm, enabledScoring = true }: Props) => {
  const [isEditing, setIsEditing] = useState(false)

  return (
    <Box gap="s8">
      <Box width="full" background="grey100" padding="s16" borderRadius={{ all: 'abs008' }}>
        {isEditing ? (
          evaluationForm ? (
            <EvaluationFormEdit
              feedback={feedback}
              evaluationForm={evaluationForm}
              onEditFinish={() => setIsEditing(false)}
            />
          ) : (
            <FeedbackEdit
              feedback={feedback}
              onEditFinish={() => setIsEditing(false)}
              enabledScoring={enabledScoring}
            />
          )
        ) : evaluationForm ? (
          <EvaluationFormDisplay
            feedback={feedback}
            evaluationForm={evaluationForm}
            onEdit={() => setIsEditing(true)}
          />
        ) : (
          <FeedbackDisplay feedback={feedback} onEdit={() => setIsEditing(true)} />
        )}
      </Box>

      <Text.Normal color="grey800" size="200">
        <FeedbackCreationInformation feedback={feedback} />
      </Text.Normal>
    </Box>
  )
}
